// Helper de diagnostico: captura eventos do cliente (medico/paciente) e envia
// pra POST /api/public/diagnostics/batch. E best-effort - nunca quebra a UI
// se o endpoint cair. Tambem loga no console no mesmo formato (severity +
// type + message) pra ajudar a debugar local em dev.
//
// Batching: eventos sao acumulados num buffer e enviados em batch a cada 5s
// ou quando o buffer atinge 20 itens. Eventos com severity=error bypassam
// o buffer (flush imediato) pra nao perder a janela de triagem. O flush de
// unload vai via sendBeacon como payload de batch unico - sobrevive ao
// fechamento da aba.
//
// Uso:
//   import { setDiagnosticsContext, logDiagnostic } from '../services/diagnostics';
//   setDiagnosticsContext({ roomId, role, identity, displayName, externalId });
//   logDiagnostic({ severity: 'error', type: 'connect_failed', message: err.message, metadata: { code: 123 } });

const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const ENDPOINT_SINGLE = `${ORIGIN}/api/public/diagnostics`;
const ENDPOINT_BATCH = `${ORIGIN}/api/public/diagnostics/batch`;

const FLUSH_INTERVAL_MS = 5_000;
const MAX_BATCH_SIZE = 20;

// Contexto da sessao atual. setDiagnosticsContext sobrescreve so os campos
// passados, preservando o resto. Util pra ir enriquecendo a medida que os
// dados aparecem (roomId chega primeiro, identity so depois do /join).
let sessionContext = {
  roomId: null,
  role: null,
  identity: null,
  displayName: null,
  externalId: null,
};

export function setDiagnosticsContext(ctx) {
  sessionContext = { ...sessionContext, ...ctx };
}

export function getDiagnosticsContext() {
  return { ...sessionContext };
}

// Buffer in-memory. Cada entry ja ta "completo" - inclui o contexto no
// momento do log, pra nao confundir se o contexto mudar entre enfileirar
// e flush.
const buffer = [];
let flushTimer = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushBuffer();
  }, FLUSH_INTERVAL_MS);
}

function buildPayload(evt) {
  return {
    ...sessionContext,
    severity: evt.severity || 'info',
    type: evt.type,
    message: typeof evt.message === 'string' ? evt.message.slice(0, 1000) : '',
    metadata: evt.metadata ? safeStringify(evt.metadata).slice(0, 8000) : null,
    userAgent: navigator.userAgent || null,
    platform: detectPlatform(),
    browser: detectBrowser(),
  };
}

function flushBuffer({ useBeacon = false } = {}) {
  if (buffer.length === 0) return;
  // Extrai e zera antes do envio - se o POST falhar, os eventos sao perdidos
  // mas nao empurram pra frente pra infinito. Observabilidade nao deve
  // bloquear outras observabilidades.
  const batch = buffer.splice(0, buffer.length);

  try {
    const body = JSON.stringify({ events: batch });

    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT_BATCH, blob)) return;
    }
    fetch(ENDPOINT_BATCH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* observabilidade nao quebra UX */ });
  } catch { /* swallow */ }
}

// Envio imediato de um unico evento - usado quando severity=error, pra nao
// perder o primeiro sinal de um incidente. Usa o endpoint antigo pra ficar
// compativel caso o backend nao tenha /batch ainda.
function sendImmediate(payload) {
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT_SINGLE, blob)) return;
    }
    fetch(ENDPOINT_SINGLE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}

/**
 * Dispara um evento de diagnostico. Normalmente entra no buffer e sobe em
 * batch a cada 5s; severity=error sobe imediato pra nao perder o primeiro
 * sinal de um incidente.
 *
 * @param {Object} evt
 * @param {'info'|'warn'|'error'} [evt.severity='info']
 * @param {string}  evt.type
 * @param {string}  [evt.message]
 * @param {Object}  [evt.metadata] - qualquer JSON (stringificado automaticamente)
 */
export function logDiagnostic({ severity = 'info', type, message, metadata } = {}) {
  if (!type) return;

  const payload = buildPayload({ severity, type, message, metadata });

  // Dev console echo (nao so em erro - info tambem ajuda durante triagem).
  // Prefixo [diag] pra filtrar facil no DevTools.
  const logFn = severity === 'error' ? 'error' : severity === 'warn' ? 'warn' : 'log';
  // eslint-disable-next-line no-console
  console[logFn](`[diag ${type}]`, message || '', metadata || '');

  if (severity === 'error') {
    // Flush do buffer junto pra o backend receber em ordem cronologica o
    // que levou ao erro (ex: network_sample warn seguido de disconnect error).
    flushBuffer();
    sendImmediate(payload);
    return;
  }

  buffer.push(payload);
  if (buffer.length >= MAX_BATCH_SIZE) {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    flushBuffer();
  } else {
    scheduleFlush();
  }
}

function safeStringify(obj) {
  try {
    return JSON.stringify(obj, (_key, val) => {
      if (val instanceof Error) return { name: val.name, message: val.message, stack: val.stack };
      return val;
    });
  } catch {
    return String(obj);
  }
}

function detectPlatform() {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Mac/i.test(ua)) return 'macos';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Linux/i.test(ua)) return 'linux';
  return 'other';
}

function detectBrowser() {
  const ua = navigator.userAgent || '';
  // Ordem importa: Edge contem "Chrome" no UA, Chrome contem "Safari", por ai vai.
  if (/Edg\//i.test(ua)) return 'edge';
  if (/OPR\//i.test(ua)) return 'opera';
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/Chrome\//i.test(ua)) return 'chrome';
  if (/Firefox\//i.test(ua)) return 'firefox';
  if (/Safari\//i.test(ua)) return 'safari';
  return 'other';
}

// Captura erros nao tratados (ex: TypeError dentro de um handler LiveKit) e
// promessas rejeitadas. Sem isso, esses casos so aparecem no console do usuario
// e a gente nunca sabe que aconteceu. Anexa UMA vez por carregamento de app.
let handlersAttached = false;
export function attachGlobalErrorHandlers() {
  if (handlersAttached) return;
  handlersAttached = true;

  window.addEventListener('error', (e) => {
    const err = e?.error;
    // Quando o script e cross-origin sem CORS o browser redige pra "Script error."
    // e zera filename/lineno. Marcamos explicito pra diferenciar de erro "de verdade".
    const isCrossOrigin = !err && (e?.message === 'Script error.' || !e?.filename);
    logDiagnostic({
      severity: 'error',
      type: 'window_error',
      message: err?.message || e?.message || 'Erro nao capturado',
      metadata: {
        name: err?.name || err?.constructor?.name || null,
        filename: e?.filename || null,
        lineno: e?.lineno ?? null,
        colno: e?.colno ?? null,
        stack: err?.stack || null,
        cause: err?.cause ? String(err.cause) : null,
        crossOrigin: isCrossOrigin || null,
        errorString: err ? String(err) : null,
      },
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason;
    const isError = reason instanceof Error;
    logDiagnostic({
      severity: 'error',
      type: 'unhandled_rejection',
      message: reason?.message || (typeof reason === 'string' ? reason : null) || 'Promise rejection',
      metadata: {
        name: reason?.name || reason?.constructor?.name || null,
        stack: reason?.stack || null,
        cause: reason?.cause ? String(reason.cause) : null,
        reasonType: isError ? 'Error' : typeof reason,
        reasonString: !isError && reason != null ? safeStringify(reason).slice(0, 2000) : null,
      },
    });
  });

  // Flush do buffer ao sair da pagina (aba fechando, refresh, navegacao).
  // pagehide e mais confiavel que beforeunload/unload em browsers modernos
  // com bfcache; visibilitychange->hidden cobre o caso mobile de voltar pro
  // home screen. Em ambos, sendBeacon garante que o POST saia mesmo que a
  // pagina ja esteja desmontando.
  window.addEventListener('pagehide', () => flushBuffer({ useBeacon: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) flushBuffer({ useBeacon: true });
  });
}

// Expoe flush manual pra testes e pra quem quiser forcar. Nao deve ser
// necessario em uso normal.
export function flushDiagnostics() {
  flushBuffer();
}
